/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, Global, jsx } from '@emotion/react'; // oxlint-disable-line typescript-eslint/consistent-type-imports -- jsx used via classic JSX pragma

import cssReset from '@atlaskit/css-reset';

import { BRAND_COLORS } from '../constants/brand-colors';

/**
 * CSS Reset + general global styling hacks for all of Design System Docs
 */
const GlobalStyles = (): jsx.JSX.Element => (
	<Global
		// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @repo/internal/react/no-css-string-literals -- Ignored via go/DSP-18766
		styles={css`
			${
				// eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values
				cssReset
			}
			* {
				box-sizing: inherit;
			}
			body,
			html {
				box-sizing: border-box;
				background-color: var(--ds-surface, #ffffff);
			}

			img {
				max-width: 100%;
			}

			/* CSS Reset sets marginTop of elements to 0 using first-child, which doesn't work with SSR.
			This override avoid margins jumping after SSR */
			:first-child:is(p, ul, ol, dl, h1, h2, h3, h4, h5, h6, blockquote, pre, form, table),
			style:first-child + :is(p, ul, ol, dl, h1, h2, h3, h4, h5, h6, blockquote, pre, form, table) {
				margin-top: 0;
			}

			#___gatsby {
				isolation: isolate; /* Hack to start new stacking context for content to prevent layering issues with portals */
			}

			/* Remove focus ring from #main-content */
			#main-content:focus {
				outline: none;
			}

			/* Navigation backdrop color and icon fill color for dark mode */
			:root {
				--nav-backdrop-color: rgba(255, 255, 255, 0.85);
				--icon-fill-color: #101214;

				--ad-color-brand-blue: ${BRAND_COLORS.blue};
				--ad-color-brand-purple: ${BRAND_COLORS.purple};
				--ad-color-brand-saffron: ${BRAND_COLORS.orange};
				--ad-color-brand-lime: ${BRAND_COLORS.lime};
				--ad-color-brand-obsidian: ${BRAND_COLORS.black};

				/* Pure black/white artwork fill, flipped by color mode further down. Done in
				   CSS rather than JS so the prerendered markup is identical for every reader
				   and hydration cannot mismatch. */
				--ad-color-artwork-fill: black;

				--ad-radius-6xlarge: 40px;
				--ad-radius-5xlarge: 32px;
				--ad-radius-4xlarge: 24px;
				--ad-radius-3xlarge: 20px;

				--motion-linear: cubic-bezier(0, 0, 1, 1); /* Linear - For loaders, progress indicators */
				--motion-ease-out: cubic-bezier(
					0,
					0.4,
					0,
					1
				); /* Ease 40 Out - Quick reaction to user intent (100-250ms) */
				--motion-ease-in-out: cubic-bezier(
					0.4,
					0,
					0,
					1
				); /* Ease 40 In-Out - On-screen transitions (250-400ms) */
				--motion-ease-in: cubic-bezier(
					0.6,
					0.01,
					0.8,
					0.6
				); /* Ease 60 In - Keyframe orchestration, followed by ease-out */
				--motion-ease-cubic: cubic-bezier(
					0.33,
					1,
					0.68,
					1
				); /* Ease Cubic Out - Natural UI transitions (200-300ms) */
				--motion-ease-spring: cubic-bezier(
					0.34,
					1.56,
					0.64,
					1
				); /* Spring - Bouncy, playful interactions */

				--motion-duration-instant: 50ms; /* Micro-interactions */
				--motion-duration-fast: 100ms; /* Quick feedback */
				--motion-duration-normal: 150ms; /* Button states */
				--motion-duration-medium: 200ms; /* Standard transitions */
				--motion-duration-slow: 250ms; /* Component animations */
				--motion-duration-slower: 400ms; /* Page transitions */
				--motion-duration-slowest: 600ms; /* Expressive animations */
				--motion-duration-reveal: 700ms; /* Scroll reveals */
				--motion-duration-dramatic: 800ms; /* Large movements */

				--ad-radius-card: var(--ad-radius-4xlarge);

				@media (min-width: 48rem) {
					--ad-radius-card: var(--ad-radius-5xlarge);
				}
				@media (min-width: 64rem) {
					--ad-radius-card: var(--ad-radius-6xlarge);
				}

				/* The leading '&' matters: a bare ':root[...]' nested here compiles to the
				   descendant selector ':root :root[...]', which never matches. */
				&[data-color-mode='dark'] {
					--ad-color-artwork-fill: white;
				}

				:root[data-color-mode='dark'] {
					background-color: var(--ds-surface, #1f1f21);
					--nav-backdrop-color: rgba(from var(--ds-surface) r g b / 0.85);
					--icon-fill-color: #e3e5e8;
				}
			}
		`}
	/>
);

export default GlobalStyles;
